import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, LoginCredentials, User } from '../../types';
import { authService } from '../../services/authService';

// Initial state
const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  accessToken: localStorage.getItem('accessToken'),
  isAuthenticated: false, // Will be set to true after checkAuth validates the token
  isLoading: !!localStorage.getItem('token'), // Start as loading if token exists
  error: null,
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      const tokenInfo = authService.parseToken(response.IdToken);
      
      // Store tokens in localStorage
      localStorage.setItem('token', response.IdToken);
      localStorage.setItem('accessToken', response.AccessToken);
      localStorage.setItem('refreshToken', response.RefreshToken);
      
      return {
        token: response.IdToken,
        accessToken: response.AccessToken,
        user: {
          userId: tokenInfo.userId,
          username: tokenInfo.username,
        },
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  localStorage.removeItem('token');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
});

export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    const token = localStorage.getItem('token');
    const accessToken = localStorage.getItem('accessToken');
    
    if (!token) {
      return rejectWithValue('No token found');
    }
    
    if (authService.isTokenExpired(token)) {
      localStorage.removeItem('token');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      return rejectWithValue('Token expired');
    }
    
    const tokenInfo = authService.parseToken(token);
    return {
      token,
      accessToken,
      user: {
        userId: tokenInfo.userId,
        username: tokenInfo.username,
      },
    };
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    // Synchronous logout for use by API interceptor (to avoid circular dependency)
    logoutSync: (state) => {
      localStorage.removeItem('token');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      state.user = null;
      state.token = null;
      state.accessToken = null;
      state.isAuthenticated = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.accessToken = action.payload.accessToken;
        state.user = action.payload.user;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.accessToken = null;
        state.isAuthenticated = false;
      })
      // Check Auth
      .addCase(checkAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.accessToken = action.payload.accessToken;
        state.user = action.payload.user;
      })
      .addCase(checkAuth.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.accessToken = null;
        state.user = null;
      });
  },
});

export const { clearError, setUser, logoutSync } = authSlice.actions;
export default authSlice.reducer;
